/**
 * TenantInvoicesTab.tsx — F7a T7.6.
 *
 * Zakładka "Faktury" w AdminTenantDetail. Faktury żyją w ADM, więc
 * (w odróżnieniu od TenantCompaniesTab/TenantUsersTab) NIE obsługuje
 * `dbUnavailable` — history przetrwa nawet soft-delete tenant DB.
 *
 * Data: GET /api/admin/tenants/:id/invoices?status=.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Receipt } from 'lucide-react';

import { Badge, type BadgeColor } from '@/components/Badge';
import { Button } from '@/components/Button';
import { ConfigurableTable } from '@/components/configurable-table';
import type { ColumnDefinition } from '@/components/configurable-table/types';
import { Skeleton } from '@/components/Skeleton';
import { PreviewTenantAuthBoundary } from '@/contexts/TenantAuthContext';
import { api } from '@/lib/api';

export type InvoiceStatus = 'draft' | 'issued' | 'paid' | 'overdue';

interface InvoiceRow {
  id: string;
  invoiceNumber: string | null;
  tenantId: string;
  subtotal: number;
  taxAmount: number;
  total: number;
  currency: string;
  status: InvoiceStatus;
  issuedAt: string | null;
  dueAt: string | null;
  paidAt: string | null;
  gcrecord: string | null;
}

interface InvoicesResponse {
  tenant: { id: string; slug: string; code: string };
  invoices: InvoiceRow[];
  total: number;
  limit: number;
  offset: number;
}

const INVOICE_STATUS_COLOR: Record<InvoiceStatus, BadgeColor> = {
  draft: 'gray',
  issued: 'blue',
  paid: 'green',
  overdue: 'red',
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatMoney(groszy: number, currency: string): string {
  try {
    return (groszy / 100).toLocaleString('pl-PL', { style: 'currency', currency });
  } catch {
    return `${(groszy / 100).toFixed(2)} ${currency}`;
  }
}

export interface TenantInvoicesTabProps {
  tenantId: string;
}

export function TenantInvoicesTab({ tenantId }: TenantInvoicesTabProps) {
  const { t } = useTranslation();
  const query = useQuery({
    queryKey: ['admin', 'tenants', tenantId, 'invoices'],
    queryFn: async (): Promise<InvoicesResponse> => {
      const res = await api.get<InvoicesResponse>(`/admin/tenants/${tenantId}/invoices`);
      return res.data;
    },
  });

  const statusOptions = useMemo(
    () => [
      { value: 'draft', label: t('admin.invoices.statusDraft') },
      { value: 'issued', label: t('admin.invoices.statusIssued') },
      { value: 'paid', label: t('admin.invoices.statusPaid') },
      { value: 'overdue', label: t('admin.invoices.statusOverdue') },
    ],
    [t],
  );

  const statusLabel = (status: InvoiceStatus): string =>
    t(`admin.invoices.status${status.charAt(0).toUpperCase() + status.slice(1)}`);

  const columns = useMemo<ColumnDefinition<InvoiceRow>[]>(
    () => [
      {
        id: 'invoiceNumber',
        accessor: (row) => row.invoiceNumber ?? '',
        header: t('admin.tenants.detail.invoices.columns.number'),
        sortable: true,
        renderCell: (_value, row) =>
          row.invoiceNumber ? (
            <span className="font-mono text-xs">{row.invoiceNumber}</span>
          ) : (
            <span className="text-xs italic text-text-muted">
              {t('admin.tenants.detail.invoices.noNumber')}
            </span>
          ),
      },
      {
        id: 'status',
        accessor: (row) => row.status,
        header: t('admin.tenants.detail.invoices.columns.status'),
        sortable: true,
        // Filtr statusu w nagłówku kolumny (client-side) — spójnie z tabelą Modułów.
        headerFilter: { category: 'select', options: statusOptions },
        renderCell: (_value, row) => (
          <Badge color={INVOICE_STATUS_COLOR[row.status]}>{statusLabel(row.status)}</Badge>
        ),
      },
      {
        id: 'total',
        accessor: (row) => row.total,
        header: t('admin.tenants.detail.invoices.columns.total'),
        sortable: true,
        filterable: false,
        align: 'right',
        renderCell: (_value, row) => (
          <span className="font-medium text-text-primary">
            {formatMoney(row.total, row.currency)}
          </span>
        ),
      },
      {
        id: 'issuedAt',
        accessor: (row) => row.issuedAt ?? '',
        header: t('admin.tenants.detail.invoices.columns.issuedAt'),
        sortable: true,
        filterable: false,
        renderCell: (_value, row) => (
          <span className="text-xs text-text-secondary">{formatDate(row.issuedAt)}</span>
        ),
      },
      {
        id: 'dueAt',
        accessor: (row) => row.dueAt ?? '',
        header: t('admin.tenants.detail.invoices.columns.dueAt'),
        filterable: false,
        renderCell: (_value, row) => (
          <span className="text-xs text-text-secondary">{formatDate(row.dueAt)}</span>
        ),
      },
      {
        id: 'paidAt',
        accessor: (row) => row.paidAt ?? '',
        header: t('admin.tenants.detail.invoices.columns.paidAt'),
        filterable: false,
        renderCell: (_value, row) => (
          <span className="text-xs text-text-secondary">{formatDate(row.paidAt)}</span>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t],
  );

  const renderActions = () => (
    <div className="flex items-center justify-end">
      <Button
        variant="ghost"
        size="sm"
        disabled
        title={t('admin.tenants.detail.invoices.detailsDisabledTooltip')}
      >
        {t('admin.tenants.detail.invoices.details')}
      </Button>
    </div>
  );

  if (query.isLoading) {
    return (
      <div className="flex flex-col gap-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} height={48} />
        ))}
      </div>
    );
  }

  if (query.isError) {
    return (
      <div
        role="alert"
        className="rounded-xl border border-red-300 bg-red-50 p-6 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300"
      >
        <p>{t('admin.tenants.detail.invoices.loadError')}</p>
        <Button variant="secondary" size="sm" className="mt-3" onClick={() => void query.refetch()}>
          {t('admin.tenants.detail.invoices.retry')}
        </Button>
      </div>
    );
  }

  const rows = query.data?.invoices ?? [];
  const emptyMessage = t('admin.tenants.detail.invoices.empty');

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h3 className="text-sm font-semibold text-text-primary">
          {t('admin.tenants.detail.invoices.title')}
        </h3>
        <p className="mt-0.5 text-xs text-text-tertiary">
          {t('admin.tenants.detail.invoices.subtitle')}
        </p>
      </div>

      <PreviewTenantAuthBoundary>
        <ConfigurableTable<InvoiceRow>
          tableId="admin-tenant-invoices"
          columns={columns}
          data={rows}
          getRowId={(row) => row.id}
          renderActions={renderActions}
          actionsColumnLabel={t('admin.tenants.detail.invoices.columns.actions')}
          persistConfig={false}
          showViewModeToggle={false}
          emptyIcon={Receipt}
          emptyTitle={emptyMessage}
          emptyDescription=""
        />
      </PreviewTenantAuthBoundary>
    </div>
  );
}
